import { Box, Divider, Text, VStack } from "@chakra-ui/react";
import {
  DataSourceType,
  ImportStatus,
} from "@syla/shared/types/models/DataSourceBase";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import { orderBy } from "lodash";
import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { useFuseSearch } from "../../../../hooks/useFuseSearch";
import { useHasVirtualKeyboard } from "../../../../hooks/useHasVirtualKeyboard";
import { useQueryDataSources } from "../../../../store/useQueryDataSources";
import { AnimatedChevronIcon } from "../../../AnimatedChevronIcon";
import { ImageWithMissingSrc } from "../../../atoms/ImageWithMissingSrc";
import { OptionContentProps } from "../../../selector/parts/OptionItem";
import { SelectOption } from "../../../selector/parts/SelectOption";
import { SelectorList } from "../../../selector/parts/SelectorList";
import { SelectorSearch } from "../../../selector/parts/SelectorSearch";
import { useGridRovingTabIndex } from "../../../selector/parts/useGridRovingTabIndex";
import { useSelectOptions } from "../../../selector/parts/useSelectOptions";
import {
  ButtonGroupItem,
  SingleSelectButtonGroup,
} from "./SingleSelectButtonGroup";

export type DataSourceTypeFilter = "all" | DataSourceType;

export type SelectDataSourceProps = {
  searchValue: string;
  filter: DataSourceTypeFilter;
  onSearchTermChange: (searchTerm: string) => void;
  onFilterChange: (filter: DataSourceTypeFilter) => void;
  onSelect: (dataSource: DataSourceWithNetworks) => void;
};

export function SelectDataSource({
  searchValue,
  filter,
  onSearchTermChange,
  onFilterChange,
  onSelect,
}: SelectDataSourceProps) {
  const dataSourcesQuery = useQueryDataSources();

  // build the fuse search once on the entire data set. This means we
  // are searching before applying the filter option.
  const searchedOptions = useFuseSearch(
    dataSourcesQuery.data,
    searchKeys,
    searchValue,
  );

  // filter the remaining data sources which match the search
  const options = useMemo(() => {
    // if the data source options haven't loaded yet, then we return early,
    // as we want to show the loading state in the dropdown.
    if (!dataSourcesQuery.data) {
      return [];
    }

    const filtered =
      filter === "all" ? searchedOptions : (
        searchedOptions.filter((source) => source.type === filter)
      );

    // apply a preferred sort order, by level of support and then name
    const sorted = orderBy(filtered, [
      (dataSource) =>
        Math.min(
          getImportStatusRank(dataSource.fileImportStatus),
          getImportStatusRank(dataSource.syncImportStatus),
        ),
      (dataSource) => dataSource.name,
    ]);

    const mapped: SelectDataSourceOption[] = sorted.map((source) => {
      const label = source.name;
      const description = dataSourceTypeMap[source.type];

      return {
        value: source._id!,
        data: {
          label,
          icon: source.image,
          description,
          hasFileImport: source.fileImportStatus !== "off",
          hasSync: source.syncImportStatus !== "off",
        },
      };
    });

    // the custom data source is always available
    mapped.push(CustomDataSourceSelectOption);

    return mapped;
  }, [dataSourcesQuery.data, searchedOptions, filter]);

  const searchRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    ([value]: string[]) => {
      if (!value) {
        return;
      }

      if (value === "customDataSource") {
        onSelect({ type: DataSourceType.Custom, name: "" });
        return;
      }

      const source = dataSourcesQuery.data?.find(
        (dataSource) => dataSource._id === value,
      );

      if (source) {
        onSelect(source);
      }
    },
    [onSelect, dataSourcesQuery.data],
  );

  const selector = useSelectOptions({
    allOptions: options,
    visibleOptions: options,
    selectedValues: EMPTY_SELECTED_VALUES,
    onChange: handleChange,
    enableMultiSelect: false,
    enableDeselect: false,
    moveSelectedToTop: false,
  });

  const focusSearchInput = useCallback(() => {
    searchRef.current?.focus();
  }, []);

  const {
    activeValue,
    focusIndex,
    containerRef,
    handleContainerKeyDown,
    handleContainerFocus,
  } = useGridRovingTabIndex({
    values: selector.sortedOptionValues,
    columnCount: 1,
    onActivate: selector.selectOption,
    onFinalArrowUp: focusSearchInput,
  });

  const handleSelectorSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          focusIndex(0);
        }
      },
      [focusIndex],
    );

  const hasVirtualKeyboard = useHasVirtualKeyboard();

  const hasAutoFocusedRef = useRef(false);
  useEffect(() => {
    if (hasAutoFocusedRef.current) {
      return;
    }

    if (!hasVirtualKeyboard) {
      setTimeout(() => searchRef.current?.focus(), 0);
      hasAutoFocusedRef.current = true;
      return;
    }

    if (selector.sortedOptions.length > 0) {
      hasAutoFocusedRef.current = true;
      focusIndex(0);
    }
  }, [selector.sortedOptions.length, focusIndex, hasVirtualKeyboard]);

  return (
    <VStack
      w="full"
      flex={1}
      alignItems="flex-start"
      spacing={0}
      overflowX="hidden"
    >
      <Box w="full" px={4} pt={4} pb={2}>
        <SelectorSearch
          searchValue={searchValue}
          onSearchChange={onSearchTermChange}
          searchRef={searchRef}
          onSearchKeyDown={handleSelectorSearchKeyDown}
        />
      </Box>
      <Box w="full" px={4} pb={2}>
        <SingleSelectButtonGroup
          options={dataSourceTypes}
          value={filter}
          onChange={onFilterChange}
          w="full"
          flexWrap="wrap"
          variant="ghost"
          borderColor="transparent"
          spacing={0}
          py={2}
          size="xs"
        />
      </Box>
      <Divider borderColor="gray.300" borderBottomWidth="1px" />

      <SelectorList
        options={options}
        sortedOptions={selector.sortedOptions}
        selectedValueSet={selector.selectedValueSet}
        activeValue={activeValue}
        isLoading={dataSourcesQuery.isLoading}
        enableDoubleColumn={false}
        wrapText={false}
        containerRef={containerRef}
        onFocus={handleContainerFocus}
        onKeyDown={handleContainerKeyDown}
        onSelect={selector.selectOption}
        OptionContent={DataSourceOptionContent}
      />
    </VStack>
  );
}

export type SelectDataSourceOptionData = {
  label?: string;
  icon?: string;
  description?: string;
  hasFileImport: boolean;
  hasSync: boolean;
};

export type SelectDataSourceOption = SelectOption<
  string,
  SelectDataSourceOptionData
>;

const getImportStatusRank = (status: ImportStatus | undefined) => {
  switch (status) {
    case ImportStatus.Off:
      return 4;
    case ImportStatus.Alpha:
      return 3;
    case ImportStatus.Beta:
      return 2;
    case ImportStatus.Gold:
      return 1;
  }
  return 4;
};

// custom option renderer for data sources.
function DataSourceOptionContent({
  option,
  wrapText,
}: OptionContentProps<SelectDataSourceOption>): React.ReactElement {
  const truncateProps =
    wrapText ?
      {
        whiteSpace: "normal" as const,
      }
    : {
        overflow: "hidden" as const,
        textOverflow: "ellipsis" as const,
        whiteSpace: "nowrap" as const,
      };

  return (
    <>
      {option.data.icon === undefined || typeof option.data.icon === "string" ?
        <ImageWithMissingSrc
          src={option.data.icon}
          alt={`${option.data.label} icon`}
          boxSize={8}
          flexShrink={0}
          loading="lazy" // only load when image is in view (as determined by browser)
          noPaddingIcon={true}
        />
      : option.data.icon}

      <Box ml={2} minW={0} alignItems="flex-start" flexGrow={1} py={1}>
        <Text
          width="full"
          fontWeight="semibold"
          fontSize="md"
          {...truncateProps}
        >
          {option.data.label}
        </Text>
        {option.data.description && (
          <Text width="full" fontSize="sm" color="gray.600" {...truncateProps}>
            {option.data.description}
          </Text>
        )}
      </Box>

      {option.data.hasFileImport && (
        // use Text instead of Tag for performance
        <Text
          px={2}
          py={0.5}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          backgroundColor="blue.50"
          color="blue.800"
        >
          File
        </Text>
      )}

      {option.data.hasSync && (
        // use Text instead of Tag for performance
        <Text
          px={2}
          py={0.5}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          backgroundColor="green.100"
          color="green.800"
        >
          Sync
        </Text>
      )}

      <AnimatedChevronIcon />
    </>
  );
}

const dataSourceTypeMap: Record<DataSourceType, string> = {
  blockchain: "Blockchain",
  custom: "Custom",
  exchange: "Exchange",
  import: "Migration",
  wallet: "Wallet",
};

const CustomDataSourceSelectOption: SelectDataSourceOption = {
  value: "customDataSource",
  keywords: ["Custom Data Source", dataSourceTypeMap[DataSourceType.Custom]],
  data: {
    label: "Custom Data Source",
    description: dataSourceTypeMap[DataSourceType.Custom],
    hasFileImport: false,
    hasSync: false,
  },
};

export const dataSourceTypes: ButtonGroupItem<DataSourceTypeFilter>[] = [
  {
    value: "all",
    label: "All",
  },
  {
    value: DataSourceType.Exchange,
    label: "Exchanges",
  },
  {
    value: DataSourceType.Wallet,
    label: "Wallets",
  },
  {
    value: DataSourceType.Blockchain,
    label: "Blockchains",
  },
  {
    value: DataSourceType.Import,
    label: "Migration",
  },
];

const EMPTY_SELECTED_VALUES: string[] = [];

// do not inline, to ensure we have a stable reference
const searchKeys = ["name", "aliases"];
